/**
 * Communication Design System Types
 *
 * Type definitions for communication design configurations that separate visual design ("bones")
 * from content generation ("blanks").
 *
 * Organization:
 * - Type definitions (interfaces, types)
 * - Type-related constants (defaults, metadata, constraints)
 * - Design system constants (blocks, templates, typography)
 *
 * Note: Template variable utilities are in @/lib/templates/variables.ts
 *       Template types are in @/types/template-domain.ts
 */

import {
  CreditCard,
  Image as ImageIcon,
  Key,
  Link as LinkIcon,
  type LucideIcon,
  MapPin,
  Megaphone,
  Minus,
  MousePointerClick,
  PanelBottom,
  PanelTop,
  Phone,
  Table,
  TextAlignStart,
  TextInitial,
  Type,
  TypeOutline,
} from 'lucide-react';
import type { components } from '@/types/platform-api';

/**
 * Locale identifier (e.g., 'en-GB', 'pt-PT', 'el-GR')
 */
export type Locale = string;

/**
 * Block ID identifier (e.g., 'body-generated-1', 'heading-generated-1', 'header')
 */
export type BlockId = string;

/**
 * Design template sources (used as starting points, not stored as variants)
 * - simple: Minimal block composition
 * - default: Standard block composition
 * - detailed: Rich block composition
 */
export type DesignTemplateSource = 'simple' | 'default' | 'detailed' | 'letters-v2-simple';

/**
 * Color scheme for communication designs
 */
export interface DesignColors {
  primary: string; // Primary brand color (e.g., #8750e5)
  background: string; // Background color (e.g., #F6F9FC)
  buttonBackground: {
    light: string;
    dark: string;
  };
  buttonText: {
    light: string;
    dark: string;
  };
  muted: string; // Muted/subtle color (e.g., #F1F5F9)
  accent: string; // Accent color for highlights (e.g., #10B981)
  text: string; // Text color (e.g., #333333)
  links: string; // Link color (e.g., #8750e5)
  border: string; // Border color (e.g., #E5E7EB)
}

/**
 * Typography configuration for communication designs
 */
export interface DesignTypography {
  fontFamily: string; // Font stack (e.g., "Inter, sans-serif")
  h1: {
    fontSize: string; // e.g., "28px"
    lineHeight: string; // e.g., "1.2"
    fontWeight: string; // e.g., "700"
  };
  h2: {
    fontSize: string; // e.g., "22px"
    lineHeight: string; // e.g., "1.3"
    fontWeight: string; // e.g., "600"
  };
  h3: {
    fontSize: string; // e.g., "18px"
    lineHeight: string; // e.g., "1.4"
    fontWeight: string; // e.g., "500"
  };
  body: {
    fontSize: string; // e.g., "16px"
    lineHeight: string; // e.g., "1.5"
    fontWeight: string; // e.g., "400"
  };
  small: {
    fontSize: string; // e.g., "14px"
    lineHeight: string; // e.g., "1.4"
    fontWeight: string; // e.g., "400"
  };
}

/**
 * Border radius options for communication designs
 */
export type BorderRadius = 'none' | 'small' | 'medium' | 'large';

/**
 * Layout configuration for communication designs (Design System - Tier 1)
 */
export interface DesignLayout {
  borderRadius: BorderRadius; // Border radius style (none: 0px, small: 4px, medium: 8px, large: 999px/full)
}

/**
 * Available email blocks that can be composed into designs
 */
/** Email block IDs — React Email components in components/email/blocks/ */
export type EmailBlock =
  | 'header'
  | 'image'
  | 'heading'
  | 'heading-static'
  | 'heading-generated'
  | 'body'
  | 'body-static'
  | 'body-generated'
  | 'callout'
  | 'cta'
  | 'access-code'
  | 'helpful-links'
  | 'separator'
  | 'footer'
  | 'payment-details'
  | 'debt-breakdown';

/** Letter block IDs — every variant is implemented by `LETTERS_V2_BLOCKS`. */
export type LetterBlock =
  | 'letter-header'
  | 'letter-address-zone'
  | 'letter-heading-generated'
  | 'letter-body-generated'
  | 'letter-date-row'
  | 'letter-access-code'
  | 'letter-contact-details'
  | 'letter-payment-details'
  | 'letter-footer';

/** Any block ID */
export type BlockType = EmailBlock | LetterBlock;

/**
 * Link item for helpful links block
 */
export interface HelpfulLinkItem {
  label: string;
  url: string;
}

/**
 * Block instance configuration
 * Maps block instance ID to its specific configuration
 *
 * Each block type has its own nested config object for clarity
 */
export interface BlockInstanceConfig {
  header?: {
    style: 'solid' | 'transparent'; // Header background style
    position?: 'center' | 'left'; // Logo/text position
    logoType?: 'logo' | 'icon'; // Use logo_full or logo_icon
    logoMode?: 'light' | 'dark'; // Use light or dark mode logo
    showLogo?: boolean; // Show company logo in header
    logoSize?: number; // Logo size (0-4, maps to 24px, 32px, 40px, 48px, 56px)
  };
  image?: {
    imageUrl?: string;
    width?: 'full-width' | 'contained';
    link?: string;
  };
  heading?: {
    content: string; // Heading text content
    level: 'h1' | 'h2' | 'h3'; // Heading level
    mode: 'generated' | 'plain'; // Generated: Can be AI-generated or manually edited per template, Plain: fixed content
    align?: 'left' | 'center' | 'right'; // Horizontal alignment (defaults to 'left')
  };
  body?: {
    content: string; // Body text content
    fontSize: 'normal' | 'small'; // Font size (normal or small only)
    mode: 'generated' | 'plain' | 'html'; // Generated: AI/template-substituted; Plain: fixed text (escaped); Html: pre-formatted markup (letter designs only — designer-controlled)
    align?: 'left' | 'center' | 'right'; // Horizontal alignment (defaults to 'left')
  };
  callout?: {
    content: string; // Callout text content
    mode: 'generated' | 'plain'; // Generated: editable per template; Plain: fixed content rendered identically across templates
    colour: 'subtle' | 'success' | 'warning'; // Visual variant
    fontSize: 'default' | 'large'; // `default` matches body text; `large` matches H3
    align: 'left' | 'center' | 'right';
  };
  cta?: {
    label: string;
    link: string;
    style: 'primary' | 'ghost';
    align?: 'left' | 'center' | 'right';
  };
  accessCode?: {
    size?: 'mini' | 'small' | 'large'; // Email only — letter ignores this
    text?: string;
    buttonText?: string; // Email only
    introText?: string; // Email only — text displayed above the access code box
    /**
     * Letter-only — render an inline QR code beside the access-code box
     * encoding `{{connect_link}}?ref={{login_code}}`. There is no standalone
     * QR block: the code renders only as part of the access-code component.
     */
    showQrCode?: boolean;
    qrSize?: number; // Pixel size (default 105)
    qrSteps?: string[]; // Optional numbered step instructions beside the QR
  };
  helpfulLinks?: {
    links: HelpfulLinkItem[]; // Links for this specific helpful-links block instance
    headerText?: string; // Optional custom header text (defaults to locale-based text)
  };
  separator?: Record<string, never>; // No configuration needed for separator - it's just a horizontal rule
  footer?: {
    showPhone?: boolean; // Show phone number
    showEmail?: boolean; // Show email address
    showAddress?: boolean; // Show company address
    showHelpCenter?: boolean; // Show help center link
    showUnsubscribe?: boolean; // Show unsubscribe link
    showTerms?: boolean; // Show terms of service link
    showPrivacy?: boolean; // Show privacy policy link
    showRegulatoryDisclosure?: boolean; // Show regulatory disclosure (FCA/ICO/company identifiers)
  };
  paymentDetails?: {
    // Email config — flexible two-column layout (logo + detail lines)
    logoUrl?: string; // Payment method logo (e.g. Multibanco, SEPA)
    lines?: Array<{ label: string; value: string }>; // Up to 5 label+value detail lines
    // Letter config — fixed payment modes
    mode?: 'multibanco' | 'iban' | 'mbway' | 'generic';
    entity?: string; // Multibanco entity (static or {{variable}})
    reference?: string; // Multibanco reference
    amount?: string; // Amount
    iban?: string; // IBAN
    bic?: string; // BIC/SWIFT
    instructions?: string; // Free-text instructions
  };
  debtBreakdown?: {
    heading?: string; // Heading text; empty falls back to the locale default
    showHeading?: boolean; // Print the heading above the receipt (default true)
    showTotal?: boolean; // Print the account total under the table (default true)
  };
  disclaimer?: {
    label: string; // Display label (e.g. "GDPR Notice")
    content: string; // Disclaimer text content (plain text — letter renderer escapes HTML)
    enabled: boolean; // Whether to render
  };
  addressZone?: Record<string, never>; // Config inherited from design's letterLayout — block is a placeholder
  spacer?: {
    height: number; // Height in mm (default 10)
    fill?: boolean; // Auto-expand to fill remaining page space (default false)
  };
  qrCode?: {
    /** @deprecated retained only for migration back-compat — letters now encode `{{connect_link}}` from the email pipeline. */
    urlTemplate?: string;
    /** @deprecated renamed to `appendReferenceCode`; retained for back-compat with v1 designs. */
    includeReference?: boolean;
    /** Append `?ref={{login_code}}` to the QR target so the portal deep-links to the case (default true). */
    appendReferenceCode?: boolean;
    /** Pixel size of the rendered PNG (default 105). */
    size?: number;
    /** Heading text shown beside the QR; localised copy comes from blockConfigs. */
    label?: string;
    /** Step instructions shown beside the QR. */
    steps?: string[];
  };
  /**
   * `letter-contact-details` block config — operator/client contact panel
   * rendered as a discrete card next to the recipient address window.
   */
  contactDetails?: {
    enabled?: boolean; // Render the panel (default true)
    heading?: string; // Panel heading (default localised "How to contact")
    showEmail?: boolean;
    showPhone?: boolean;
    showWebsite?: boolean;
    showAddress?: boolean;
  };
}

/**
 * Letter-specific layout configuration
 */
export interface LetterLayoutConfig {
  envelopeFormat:
    | 'DL'
    | 'C6'
    | 'C6/5'
    | 'C5'
    | 'C4'
    | 'DIN5008A-LEFT'
    | 'DIN5008A-RIGHT'
    | 'DIN5008B-LEFT'
    | 'DIN5008B-RIGHT'
    | 'CTT-PT-DL'
    | 'CTT-PT-DL-DOUBLE'
    | 'ADARE-C5'
    | 'custom';
  addressWindow: {
    /** `inline-*` positions render the address-zone inside the letterhead row at letterhead height; `left`/`right` keep the legacy float-below-letterhead layout. */
    position: 'left' | 'right' | 'inline-left' | 'inline-right';
    offsetX: number; // mm from edge
    offsetY: number; // mm from top
    width: number; // mm
    height: number; // mm
  };
  margins: {
    top: number; // mm
    right: number; // mm
    bottom: number; // mm
    left: number; // mm
  };
  disclaimers: Array<{
    id: string;
    label: string;
    content: string;
    enabled: boolean;
  }>;
}

/**
 * Component-level configuration (Tier 2)
 *
 * Global defaults that apply to specific component types
 */
export interface DesignComponents {
  blocks: string[]; // Ordered array of block IDs (defines rendering order)
  blockConfigs: Record<string, BlockInstanceConfig>; // Per-instance configuration
}

/**
 * Complete design configuration (stored in JSONB)
 * Typography is NOT stored - it's hardcoded in the app
 */
export interface CommunicationDesign {
  colors: DesignColors | null;
  layout: DesignLayout | null;
  components: DesignComponents;
}

/**
 * Complete communication design with typography (runtime only, not stored in DB)
 */
export interface CommunicationDesignWithTypography extends CommunicationDesign {
  typography: DesignTypography;
}

/**
 * Base design settings (colors, layout, variables) without block composition
 * Used as a base type for converting brand configuration to design settings format
 */
export interface DesignBaseSettings {
  colors: DesignColors | null; // Design colors (null if inherited from brand config)
  layout: DesignLayout | null; // Design layout (null if inherited from brand config)
  variables: {
    company_name: string | null;
    company_phone: string | null;
    company_email: string | null;
    company_address: string | null; // Full address for footer
    logo_url: string | null; // Deprecated: use logo_full_url_light/dark or logo_icon_url_light/dark
    logo_full_url_light: string | null;
    logo_full_url_dark: string | null;
    logo_icon_url_light: string | null;
    logo_icon_url_dark: string | null;
    support_url: string | null;
    support_email: string | null; // Support email for customer-facing communications
    support_phone: string | null; // Support phone for customer-facing communications
    support_address: string | null; // Support mailing address for customer correspondence
    unsubscribe_url_template: string | null;
    privacy_policy_url: string | null;
    terms_url: string | null;
  } | null; // Company info and links (null if inherited from brand config)
}

/**
 * Design-specific settings for email designs
 * Includes colors, layout, variables, and block composition
 * Colors, layout, and variables can be null to inherit from brand configuration
 */
export interface DesignSettings extends DesignBaseSettings {
  channel?: DesignChannel; // Design channel — 'email' (default) or 'letter'
  blocks: string[]; // Ordered array of block IDs
  blockConfigs: Record<string, BlockInstanceConfig>; // Per-instance configuration
  letterLayout?: LetterLayoutConfig | null; // Letter-specific layout config (null for email designs)
  footerDisclaimer?: string; // Optional disclaimer rendered in the letter footer
}

/**
 * Email design configuration entity (database record)
 * Extends the Platform API's DesignResponse with domain-specific types
 */
export interface CommunicationDesignConfig
  extends Omit<
    components['schemas']['DesignResponse'],
    | 'object'
    | 'request_id'
    | 'is_global_settings_record'
    | 'global_settings'
    | 'settings'
    | 'variable_overrides'
    | 'status'
    | 'created_at'
    | 'updated_at'
  > {
  is_global_settings_record: boolean; // Override: non-nullable instead of boolean | null (deprecated - no longer used)
  global_settings: null; // Deprecated - always null, kept for type compatibility
  settings: DesignSettings; // Override: typed JSONB structure instead of unknown
  variable_overrides: Record<string, unknown> | null; // Override: typed JSONB structure instead of unknown | null
  status: 'active' | 'archived'; // Override: typed enum instead of string | null
  created_at: string; // Override: ISO string instead of Date | null
  updated_at: string; // Override: ISO string instead of Date | null
}

/**
 * Form data for creating/editing user design configs
 */
export interface CommunicationDesignConfigFormData {
  name: string;
  debt_type_id: string | null;
  client_id: string | null;
  settings: DesignSettings;
  channel?: DesignChannel;
}

/**
 * Design template metadata (hardcoded presets)
 */
export interface DesignTemplate {
  id: DesignTemplateSource;
  name: string;
  description: string;
  icon: string; // Icon component name
  previewBlocks: string[]; // Block composition for this template
  blockConfigs?: Record<string, BlockInstanceConfig>; // Optional per-template block configurations
  channel: DesignChannel; // Which channel this template is for
}

/**
 * Maximum number of custom designs allowed
 */
export const MAX_CUSTOM_DESIGNS = 5;

/**
 * Hardcoded typography configuration (not stored in database)
 */
export const DEFAULT_TYPOGRAPHY: DesignTypography = {
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  h1: { fontSize: '28px', lineHeight: '1.2', fontWeight: '700' },
  h2: { fontSize: '22px', lineHeight: '1.3', fontWeight: '600' },
  h3: { fontSize: '18px', lineHeight: '1.4', fontWeight: '500' },
  body: { fontSize: '16px', lineHeight: '1.5', fontWeight: '400' },
  small: { fontSize: '14px', lineHeight: '1.4', fontWeight: '400' },
};

/**
 * Default design configuration values (stored in database)
 */
export const DEFAULT_COMMUNICATION_DESIGN: CommunicationDesign = {
  colors: null,
  layout: null,
  components: {
    blocks: ['header', 'heading-1', 'body-1', 'footer'],
    blockConfigs: {
      header: {
        header: {
          style: 'solid',
        },
      },
      'heading-1': {
        heading: {
          content: '',
          level: 'h2',
          mode: 'generated',
        },
      },
      'body-1': {
        body: {
          content: '',
          fontSize: 'normal',
          mode: 'generated',
        },
      },
      footer: {
        footer: {
          showPhone: true,
          showEmail: true,
          showAddress: false,
          showHelpCenter: false,
          showUnsubscribe: true,
          showTerms: false,
          showPrivacy: true,
        },
      },
    },
  },
};

/**
 * Default base design settings (empty values with default colors and layout)
 * Used as fallback when brand configuration is not available
 */
export const DEFAULT_DESIGN_BASE_SETTINGS: DesignBaseSettings = {
  colors: {
    primary: '#8750e5',
    background: '#F6F9FC',
    muted: '#F3F4F6',
    accent: '#F1E8FA',
    text: '#333333',
    links: '#8750e5',
    border: '#E5E7EB',
    buttonBackground: {
      light: '#8750e5',
      dark: '#8750e5',
    },
    buttonText: {
      light: '#ffffff',
      dark: '#ffffff',
    },
  },
  layout: {
    borderRadius: 'medium',
  },
  variables: {
    company_name: null,
    company_phone: null,
    company_email: null,
    company_address: null,
    logo_url: null,
    logo_full_url_light: null,
    logo_full_url_dark: null,
    logo_icon_url_light: null,
    logo_icon_url_dark: null,
    support_url: null,
    support_email: null,
    support_phone: null,
    support_address: null,
    unsubscribe_url_template: null,
    privacy_policy_url: null,
    terms_url: null,
  },
};

/**
 * Font options for font selector
 */
export const FONT_OPTIONS = [
  {
    value: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    label: 'Inter (Default)',
  },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
] as const;

/**
 * Block metadata for the block library
 */
export type DesignChannel = 'email' | 'letter';

export interface BlockMetadata {
  id: BlockType;
  name: string;
  description: string;
  category: 'structure' | 'content' | 'action';
  configurableProps?: string[];
  allowMultiple?: boolean;
  required?: boolean;
  /**
   * Stronger than `required`: a locked block cannot be deleted, reordered, or re-added.
   * Used by the react-pdf letter channel, whose layout is fixed except for the
   * generated heading/body slots.
   */
  locked?: boolean;
  /**
   * The block's position is fixed (it cannot be reordered, and when added it
   * always lands at its canonical slot just above the footer) but, unlike a
   * `locked` block, it can be deleted and re-added. Used for the letter
   * Payment Details and Access Code blocks.
   */
  positionFixed?: boolean;
  icon: LucideIcon;
}

/** Email blocks — React Email components in components/email/blocks/ */
export const EMAIL_BLOCKS: BlockMetadata[] = [
  {
    id: 'header',
    name: 'Header',
    description: 'Logo or company name with branded background',
    category: 'structure',
    configurableProps: ['headerStyle', 'showLogo'],
    icon: PanelTop,
  },
  {
    id: 'heading',
    name: 'Heading',
    description: 'Heading text (H1, H2, H3). Toggle between fixed and dynamic in block settings.',
    category: 'content',
    configurableProps: ['mode', 'content', 'level'],
    allowMultiple: true,
    icon: Type,
  },
  {
    id: 'body',
    name: 'Body Text',
    description: 'Body text. Toggle between fixed and dynamic in block settings.',
    category: 'content',
    configurableProps: ['mode', 'content', 'fontSize'],
    allowMultiple: true,
    icon: TextInitial,
  },
  {
    id: 'callout',
    name: 'Callout',
    description: 'Highlighted message panel for promos, notices, or call-outs',
    category: 'content',
    configurableProps: ['mode', 'content', 'colour', 'fontSize', 'align'],
    allowMultiple: true,
    icon: Megaphone,
  },
  {
    id: 'heading-static',
    name: 'Fixed Heading',
    description: 'Legacy fixed heading — kept for backward compatibility with existing designs.',
    category: 'content',
    configurableProps: ['content', 'level'],
    allowMultiple: true,
    icon: Type,
  },
  {
    id: 'heading-generated',
    name: 'Dynamic Heading',
    description: 'Legacy dynamic heading — kept for backward compatibility with existing designs.',
    category: 'content',
    configurableProps: ['content', 'level'],
    allowMultiple: true,
    icon: TypeOutline,
  },
  {
    id: 'body-static',
    name: 'Fixed Body Text',
    description: 'Legacy fixed body — kept for backward compatibility with existing designs.',
    category: 'content',
    configurableProps: ['content', 'fontSize'],
    allowMultiple: true,
    icon: TextInitial,
  },
  {
    id: 'body-generated',
    name: 'Dynamic Body Text',
    description: 'Legacy dynamic body — kept for backward compatibility with existing designs.',
    category: 'content',
    configurableProps: ['content', 'fontSize'],
    allowMultiple: true,
    icon: TextAlignStart,
  },
  {
    id: 'image',
    name: 'Image',
    description: 'Image block with configurable width',
    category: 'content',
    configurableProps: ['imageUrl', 'width'],
    icon: ImageIcon,
  },
  {
    id: 'cta',
    name: 'Button',
    description: 'Action button with link',
    category: 'action',
    configurableProps: ['ctaLabel', 'ctaLink', 'ctaStyle'],
    allowMultiple: true,
    icon: MousePointerClick,
  },
  {
    id: 'access-code',
    name: 'Access Code',
    description: '6-digit authentication code display',
    category: 'content',
    // Deliberately not `required`: a design may legitimately have no access code
    // (see the Simple template). Deleting it warns instead — BlockComposition.
    icon: Key,
  },
  {
    id: 'helpful-links',
    name: 'Helpful Links',
    description: 'Quick links to support resources',
    category: 'action',
    configurableProps: ['helpfulLinks'],
    icon: LinkIcon,
  },
  { id: 'separator', name: 'Separator', description: 'Horizontal rule separator', category: 'structure', allowMultiple: true, icon: Minus },
  {
    id: 'footer',
    name: 'Footer',
    description: 'Contact info, legal links, and unsubscribe',
    category: 'structure',
    configurableProps: ['footerStyle', 'showUnsubscribe'],
    required: true,
    icon: PanelBottom,
  },
  {
    id: 'payment-details',
    name: 'Payment Details',
    description: 'Payment reference display with logo and detail lines',
    category: 'content',
    configurableProps: ['logoUrl', 'lines'],
    allowMultiple: true,
    icon: CreditCard,
  },
  {
    id: 'debt-breakdown',
    name: 'Debt Breakdown',
    description: 'A receipt per debt: creditor, reference, original amount, principal, interest, fees, remaining',
    category: 'content',
    configurableProps: ['showHeading', 'heading', 'showTotal'],
    icon: Table,
  },
];

/**
 * Letter blocks (react-pdf) — the layout is locked: every block is fixed in
 * place except the generated heading/body slots.
 */
export const LETTERS_V2_BLOCKS: BlockMetadata[] = [
  {
    id: 'letter-header',
    name: 'Letterhead',
    description: 'Company letterhead with logo',
    category: 'structure',
    locked: true,
    icon: PanelTop,
  },
  {
    id: 'letter-address-zone',
    name: 'Address Zone',
    description: 'Recipient address block for windowed envelopes',
    category: 'structure',
    locked: true,
    icon: MapPin,
  },
  {
    id: 'letter-date-row',
    name: 'Date Row',
    description: 'Right-aligned date row filled with the send date',
    category: 'content',
    locked: true,
    icon: Type,
  },
  {
    id: 'letter-heading-generated',
    name: 'Dynamic Heading',
    description: 'Heading text editable per template',
    category: 'content',
    allowMultiple: true,
    icon: TypeOutline,
  },
  {
    id: 'letter-body-generated',
    name: 'Dynamic Body Text',
    description: 'Body text editable per template',
    category: 'content',
    allowMultiple: true,
    icon: TextAlignStart,
  },
  {
    id: 'letter-payment-details',
    name: 'Payment Details',
    description: 'Payment reference display (Multibanco, IBAN, MB Way)',
    category: 'content',
    positionFixed: true,
    icon: CreditCard,
  },
  {
    id: 'letter-contact-details',
    name: 'Contact Details',
    description: 'How-to-contact panel (email, phone, website) beside the address',
    category: 'content',
    locked: true,
    icon: Phone,
  },
  {
    id: 'letter-access-code',
    name: 'Access Code',
    description: 'Login code and portal link',
    category: 'content',
    positionFixed: true,
    icon: Key,
  },
  {
    id: 'letter-footer',
    name: 'Footer',
    description: 'Contact info and legal text',
    category: 'structure',
    locked: true,
    icon: PanelBottom,
  },
];

/** Get blocks for a specific channel */
export function getBlocksForChannel(channel: DesignChannel): BlockMetadata[] {
  // The letter channel renders exclusively via the react-pdf pipeline, whose
  // layout is fixed (locked/position-fixed blocks).
  return channel === 'letter' ? LETTERS_V2_BLOCKS : EMAIL_BLOCKS;
}

/** Find block metadata by ID within a specific channel */
export function findBlockMetadata(blockId: BlockType, channel: DesignChannel): BlockMetadata | undefined {
  return getBlocksForChannel(channel).find((b) => b.id === blockId);
}

/**
 * Hardcoded design templates (starting points for user designs)
 * These are NOT stored in the database - they're templates for creating new designs
 */
export const DESIGN_TEMPLATES: DesignTemplate[] = [
  {
    id: 'simple',
    name: 'Simple',
    description: 'Minimal design',
    icon: 'Square',
    channel: 'email',
    previewBlocks: ['header', 'heading-1', 'body-1', 'footer'],
    // Optional: Custom block configurations for this template
    // If not provided, defaults will be generated automatically
    blockConfigs: {
      header: {
        header: {
          style: 'transparent',
          position: 'left',
          logoType: 'logo',
          logoMode: 'light',
          showLogo: true,
        },
      },
      'heading-1': {
        heading: {
          content: '',
          level: 'h2',
          mode: 'generated',
        },
      },
      'body-1': {
        body: {
          content: '',
          fontSize: 'normal',
          mode: 'generated',
        },
      },
      footer: {
        footer: {
          showPhone: true,
          showEmail: true,
          showAddress: false,
          showHelpCenter: false,
          showUnsubscribe: true,
          showTerms: false,
          showPrivacy: true,
        },
      },
    },
  },
  {
    id: 'default',
    name: 'Default',
    description: 'Standard email',
    icon: 'Layers2',
    channel: 'email',
    previewBlocks: ['header', 'heading-1', 'body-1', 'access-code', 'separator-1', 'helpful-links', 'body-2', 'footer'],
    // Optional: Custom block configurations for this template
    blockConfigs: {
      header: {
        header: {
          style: 'solid',
          position: 'center',
          logoType: 'logo',
          logoMode: 'dark',
          showLogo: true,
        },
      },
      'heading-1': {
        heading: {
          content: '',
          level: 'h2',
          mode: 'generated',
        },
      },
      'body-1': {
        body: {
          content: '',
          fontSize: 'normal',
          mode: 'generated',
        },
      },
      'access-code': {
        accessCode: {
          size: 'large',
        },
      },
      'separator-1': {
        separator: {},
      },
      'helpful-links': {
        helpfulLinks: {
          links: [
            { label: 'Help Center', url: '#' },
            { label: 'Contact Support', url: '#' },
          ],
        },
      },
      'body-2': {
        body: {
          content: '',
          fontSize: 'normal',
          mode: 'generated',
        },
      },
      footer: {
        footer: {
          showPhone: true,
          showEmail: true,
          showAddress: false,
          showHelpCenter: false,
          showUnsubscribe: true,
          showTerms: false,
          showPrivacy: true,
        },
      },
    },
  },
  {
    id: 'detailed',
    name: 'Detailed',
    description: 'Rich email',
    icon: 'Layers',
    channel: 'email',
    previewBlocks: [
      'header',
      'image-1',
      'heading-1',
      'body-1',
      'body-2',
      'cta-1',
      'separator-1',
      'access-code',
      'separator-2',
      'helpful-links',
      'footer',
    ],
    // Optional: Custom block configurations for this template
    blockConfigs: {
      header: {
        header: {
          style: 'solid',
          position: 'center',
          logoType: 'logo',
          logoMode: 'dark',
          showLogo: true,
        },
      },
      'image-1': {
        image: {
          imageUrl: '',
          width: 'full-width',
        },
      },
      'heading-1': {
        heading: {
          content: '',
          level: 'h1',
          mode: 'generated',
        },
      },
      'body-1': {
        body: {
          content: '',
          fontSize: 'normal',
          mode: 'generated',
        },
      },
      'body-2': {
        body: {
          content: '',
          fontSize: 'normal',
          mode: 'generated',
        },
      },
      'cta-1': {
        cta: {
          label: 'Get Started',
          link: '#',
          style: 'primary',
          align: 'center',
        },
      },
      'access-code': {
        accessCode: {
          size: 'large',
        },
      },
      'separator-2': {
        separator: {},
      },
      'separator-1': {
        separator: {},
      },
      'helpful-links': {
        helpfulLinks: {
          links: [
            { label: 'Help Center', url: '#' },
            { label: 'Contact Support', url: '#' },
            { label: 'Documentation', url: '#' },
          ],
        },
      },
      footer: {
        footer: {
          showPhone: true,
          showEmail: true,
          showAddress: true,
          showHelpCenter: true,
          showUnsubscribe: true,
          showTerms: true,
          showPrivacy: true,
        },
      },
    },
  },
  // Letter design template — locked react-pdf layout; only the generated heading/body slots are editable.
  {
    id: 'letters-v2-simple',
    name: 'Simple',
    description: 'Locked PDF letter',
    icon: 'Square',
    channel: 'letter',
    previewBlocks: [
      'letter-header',
      'letter-address-zone',
      'letter-contact-details',
      'letter-date-row',
      'letter-heading-generated-1',
      'letter-body-generated-1',
      'letter-payment-details-1',
      'letter-access-code',
      'letter-footer',
    ],
    blockConfigs: {
      'letter-contact-details': {
        contactDetails: { enabled: true, showEmail: true, showPhone: true, showWebsite: true, showAddress: false },
      },
      'letter-heading-generated-1': {
        heading: { content: '', level: 'h2', mode: 'generated' },
      },
      'letter-body-generated-1': {
        body: { content: '', fontSize: 'normal', mode: 'generated' },
      },
      'letter-payment-details-1': {
        paymentDetails: {
          mode: 'multibanco',
          entity: '{{multibanco_entity_number}}',
          reference: '{{multibanco_reference_number}}',
          amount: '{{total_amount}}',
        },
      },
      'letter-access-code': {
        accessCode: { text: '' },
      },
    },
  },
];
